import { z } from 'zod';
import { ActiveCombatEffectKey, } from "../combat/types.js";
import { EntityStatus, EntityType } from "../entity/types.js";
import { TerrainType, FeatureType } from "../hex/types.js";
export const CombatPositionSchema = z.object({
    x: z.number().int(),
    y: z.number().int(),
});
export const BattlefieldClientAssetSchema = z.object({
    key: z.string(),
    url: z.string(),
});
export const BattlefieldClientAssetsSchema = z.object({
    mapUrl: z.string(),
    tilesets: z.array(BattlefieldClientAssetSchema),
    images: z.array(BattlefieldClientAssetSchema),
});
export const BattlefieldSpawnGroupSchema = z.object({
    groupId: z.string(),
    positions: z.array(CombatPositionSchema),
});
export const BattlefieldDefSchema = z.object({
    id: z.string(),
    width: z.number().int().min(1),
    height: z.number().int().min(1),
    terrain: z.array(z.nativeEnum(TerrainType)),
    features: z.array(z.nativeEnum(FeatureType)),
    river: z.boolean(),
    road: z.boolean(),
    blockedMove: z.array(CombatPositionSchema),
    blockedLos: z.array(CombatPositionSchema),
    spawnGroups: z.array(BattlefieldSpawnGroupSchema),
    clientAssets: BattlefieldClientAssetsSchema,
});
export const GetBattlefieldDefRequestSchema = z.object({ id: z.string() });
export const GetBattlefieldDefResponseSchema = BattlefieldDefSchema;
export const ActiveCombatEffectSchema = z.object({
    key: z.nativeEnum(ActiveCombatEffectKey),
    sourceDefId: z.string(),
    amount: z.number().int(),
    expiresRound: z.number().int().min(1),
});
export const EntityCombatStateSchema = z.object({
    entityType: z.nativeEnum(EntityType),
    entityId: z.string(),
    archetypeId: z.string().optional(),
    groupId: z.string(),
    position: CombatPositionSchema,
    status: z.nativeEnum(EntityStatus).default(EntityStatus.NORMAL),
    activeEffects: z.array(ActiveCombatEffectSchema).default([]),
});
export const CombatRollSchema = z.object({
    roll: z.number().int(),
    modifiers: z.array(z.number().int()),
    target: z.number().int(),
});
export const CombatLogAbilityHitSchema = z.object({
    targetId: z.string(),
    hitRoll: CombatRollSchema.optional(),
    damage: z.number().int(),
    resultText: z.string(),
});
export const CombatLogAbilityUseSchema = z.object({
    abilityId: z.string(),
    targets: z.array(CombatLogAbilityHitSchema),
    targetPosition: CombatPositionSchema.optional(),
});
export const CombatLogItemUseSchema = z.object({
    defId: z.string(),
    name: z.string(),
    useText: z.string(),
});
export const CombatLogEntrySchema = z.object({
    entityId: z.string(),
    abilityUsed: CombatLogAbilityUseSchema.optional(),
    itemUsed: CombatLogItemUseSchema.optional(),
    resultText: z.string(),
    timestamp: z.number().int(),
});
export const EnemyStateSchema = z.object({
    entityId: z.string(),
    name: z.string(),
    currentHP: z.number().int(),
    currentMP: z.number().int(),
    armor: z.number().int().min(0).default(0),
    sprite: z.string().default(''),
});
export const CombatStateSchema = z.object({
    id: z.string(),
    encounterId: z.string(),
    battlefieldId: z.string(),
    round: z.number().int().min(1),
    turnIndex: z.number().int().min(0).default(0),
    turnHasMoved: z.boolean().default(false),
    turnHasUsedAbility: z.boolean().default(false),
    groupOrder: z.array(z.string()).default([]),
    entityStates: z.array(EntityCombatStateSchema),
    enemyStates: z.array(EnemyStateSchema).default([]),
    log: z.array(CombatLogEntrySchema).default([]),
    status: z.enum(['active', 'victory', 'defeat']).default('active'),
});
// Endpoint DTOs
export const GetCombatStateRequestSchema = z.object({ id: z.string() });
export const GetCombatStateResponseSchema = CombatStateSchema;
export const CombatDeltaSchema = z.object({
    round: z.number().int().min(1).optional(),
    turnIndex: z.number().int().min(0).optional(),
    turnHasMoved: z.boolean().optional(),
    turnHasUsedAbility: z.boolean().optional(),
    status: z.enum(['active', 'victory', 'defeat']).optional(),
});
export const EntityCombatStateDeltaSchema = z.object({
    entityId: z.string(),
    position: CombatPositionSchema.optional(),
    currentHP: z.number().int().optional(),
    currentMP: z.number().int().optional(),
    status: z.nativeEnum(EntityStatus).optional(),
    // Sent whole rather than as an add/remove pair: the list is short and a replacement
    // is the only way the client can learn about an expiry as well as a new buff.
    activeEffects: z.array(ActiveCombatEffectSchema).optional(),
});
export const SubmitTurnRequestSchema = z.object({
    id: z.string(), // combat id
    moveTo: CombatPositionSchema.optional(), // optional movement before or after ability
    abilityId: z.string(),
    targetEntityId: z.string().optional(),
});
export const SubmitTurnResponseSchema = z.object({
    combatId: z.string(),
    combatDelta: CombatDeltaSchema,
    entityStateDeltas: z.array(EntityCombatStateDeltaSchema).default([]),
    actions: z.array(CombatLogEntrySchema).default([]),
    victory: z.boolean().default(false),
    defeat: z.boolean().default(false),
});
// Shared response for move/useAbility/endTurn actions
export const CombatActionResponseSchema = z.object({
    combatId: z.string(),
    combatDelta: CombatDeltaSchema,
    entityStateDeltas: z.array(EntityCombatStateDeltaSchema).default([]),
    actions: z.array(CombatLogEntrySchema).default([]),
    victory: z.boolean().default(false),
    defeat: z.boolean().default(false),
    turnEnded: z.boolean().default(false),
});
// POST /game/combat/:id/move
export const CombatMoveRequestSchema = z.object({
    id: z.string(), // combat id
    moveTo: CombatPositionSchema,
});
// POST /game/combat/:id/use-ability
export const CombatUseAbilityRequestSchema = z.object({
    id: z.string(), // combat id
    abilityId: z.string(),
    targetEntityId: z.string().optional(),
    targetPosition: CombatPositionSchema.optional(),
});
// POST /game/combat/use-item
// Targeting is optional: potions need none, while Phase 5's ability scrolls reuse the
// same target fields as an ability cast.
export const CombatUseItemRequestSchema = z.object({
    id: z.string(), // combat id
    instanceId: z.string(),
    targetEntityId: z.string().optional(),
    targetPosition: CombatPositionSchema.optional(),
});
// POST /game/combat/:id/end-turn
export const CombatEndTurnRequestSchema = z.object({
    id: z.string(), // combat id
});
// POST /game/combat/cheat-end — cheat to instantly end combat in a win or a loss.
// Available to all players during early development.
export const CombatCheatEndRequestSchema = z.object({
    id: z.string(), // combat id
    outcome: z.enum(['win', 'lose']),
});
// --- Combat Resolution DTOs ---
// POST /game/combat/resolve
export const CombatResolveRequestSchema = z.object({
    combatId: z.string(),
    action: z.enum(['revive', 'quit']).optional(), // only required on defeat
});
